/**
 * WorkspaceGrid — Full workspace-level grid with selection, bulk actions,
 * density toggle, saved views, and inline cell editing.
 *
 * Drop-in replacement for UniversalGrid that adds operational tooling.
 */

import { useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { useGridSettings, type RowDensity } from '@/hooks/useGridSettings';
import { UniversalGrid, type RefResolverHandle } from './UniversalGrid';
import { BulkActionBar } from './BulkActionBar';
import { SavedViewSelector, type SavedViewConfig } from './SavedViewSelector';
import { Button } from '@/components/ui/button';
import { Rows3 } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { EntityMeta } from '@/types/entity';
import type { ListColumnDSL } from '@/lib/use-view-template';
import { summarizeValue } from '@/lib/format';

type DataRow = Record<string, unknown>;

// ─── Types ─────────────────────────────────────────

export interface WorkspaceGridProps {
  slug: string;
  meta: EntityMeta;
  columns: ListColumnDSL[];
  data: DataRow[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onSort: (field: string, order: 'asc' | 'desc') => void;
  onRowClick?: (row: DataRow) => void;
  onRefresh?: () => void;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  loading?: boolean;
  /** Enable row selection + bulk actions (default: true) */
  selectable?: boolean;
  /** Enable inline cell editing (default: true) */
  editable?: boolean;
  /** Show density toggle (default: true) */
  showDensity?: boolean;
  /** Show saved view selector (default: true) */
  showSavedViews?: boolean;
  /** Current view config for saved views */
  currentViewConfig?: SavedViewConfig;
  /** Callback when a saved view is applied */
  onApplyView?: (config: SavedViewConfig) => void;
  refResolver?: RefResolverHandle;
  /** Extra toolbar content rendered between view selector and density */
  toolbarExtra?: React.ReactNode;
  onQuickEdit?: (row: DataRow) => void;
  onCellEditOverride?: (rowId: string, field: string, value: unknown) => Promise<void>;
  rowClassName?: (row: DataRow) => string | undefined;
  /** Raw TanStack ColumnDef columns prepended after the selection checkbox. */
  prependColumns?: ColumnDef<DataRow>[];
}

// ─── Density labels ────────────────────────────────

const DENSITY_LABELS: Record<RowDensity, string> = {
  compact: 'Compact',
  default: 'Default',
  comfortable: 'Comfortable',
};

// ─── Component ─────────────────────────────────────

export function WorkspaceGrid({
  slug,
  meta,
  columns,
  data,
  total,
  page,
  pageSize,
  onPageChange,
  onSort,
  onRowClick,
  onRefresh,
  sortField,
  sortOrder,
  loading,
  selectable = true,
  editable = true,
  showDensity = true,
  showSavedViews = true,
  currentViewConfig,
  onApplyView,
  refResolver,
  toolbarExtra,
  onQuickEdit,
  onCellEditOverride,
  rowClassName,
  prependColumns,
}: WorkspaceGridProps) {
  const toast = useToast();
  const { density, cycleDensity } = useGridSettings(`ws-${slug}`);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // ─── Inline cell edit handler ─────────────────────
  const handleCellEdit = useCallback(
    async (rowId: string, field: string, value: unknown) => {
      try {
        if (onCellEditOverride) {
          await onCellEditOverride(rowId, field, value);
        } else {
          await api.patch(`/entities/${slug}/${rowId}`, {
            [field]: value,
            _source: 'workspace_grid',
            _reason: {
              type: 'inline_edit',
              summary: `Inline edit ${slug}.${field}`,
            },
          });
        }
        onRefresh?.();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update');
        throw err;
      }
    },
    [onCellEditOverride, slug, onRefresh, toast],
  );

  const handleClearSelection = useCallback(() => setSelectedIds([]), []);
  const handleRefresh = useCallback(() => {
    setSelectedIds([]);
    onRefresh?.();
  }, [onRefresh]);

  const handleCopyRow = useCallback(async (row: DataRow) => {
    const payload = columns.map((column) => {
      const value = row[column.field];
      return `${column.label ?? column.field}: ${summarizeValue(value)}`;
    }).join('\n');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      }
      toast.success('本行已复制');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '复制失败');
    }
  }, [columns, toast]);

  // ─── Toolbar (density + saved views) ──────────────
  const showToolbar = showDensity || (showSavedViews && onApplyView) || toolbarExtra;

  return (
    <div className="space-y-1">
      {showToolbar && (
        <div className="flex items-center justify-end gap-2 mb-1">
          {showSavedViews && onApplyView && (
            <SavedViewSelector
              entitySlug={slug}
              currentConfig={currentViewConfig ?? { sort: sortField, order: sortOrder }}
              onApply={onApplyView}
            />
          )}
          {toolbarExtra}
          {showDensity && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs text-muted-foreground"
              onClick={cycleDensity}
              title={`Row density: ${DENSITY_LABELS[density]}`}
            >
              <Rows3 className="h-3.5 w-3.5" />
              {DENSITY_LABELS[density]}
            </Button>
          )}
        </div>
      )}

      <UniversalGrid
        slug={slug}
        meta={meta}
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onSort={onSort}
        onRowClick={onRowClick}
        sortField={sortField}
        sortOrder={sortOrder}
        loading={loading}
        density={density}
        onSelectionChange={selectable ? setSelectedIds : undefined}
        onCellEdit={editable ? handleCellEdit : undefined}
        refResolver={refResolver}
        onQuickEdit={onQuickEdit}
        onQuickCopyRow={handleCopyRow}
        rowClassName={rowClassName}
        prependColumns={prependColumns}
      />

      {selectable && (
        <BulkActionBar
          slug={slug}
          meta={meta}
          selectedIds={selectedIds}
          onClearSelection={handleClearSelection}
          onRefresh={handleRefresh}
        />
      )}
    </div>
  );
}
